/**
 * Staff portal — My Leave Requests
 *
 * Mirrors [admin/leaves/index.tsx] but scoped to the current staff user:
 *  - Header with Title + dimmed subtitle and a refresh ActionIcon
 *  - 4 clickable Stats cards (Total / Pending / Approved / Rejected)
 *  - Toolbar with search + Apply-for-leave button + view mode toggle
 *    (Table / Item / Grid) persisted under `hr.my.leaves` in viewModeStore.
 *  - Default sort: latest application first.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Card,
  SimpleGrid,
  ThemeIcon,
  Paper,
  Button,
  Tooltip,
  SegmentedControl,
  Grid,
  Alert,
  Loader,
  Divider,
} from '@/components/ui';
import { TextInput } from '@/components/ui/inputs';
import ActionIcon from '@/components/ui/ActionIcon';
import { DataTable, type DataTableColumn } from '@/components/advanced';
import { FadeIn } from '@/components/animations';
import { PageHelpButton } from '@/components/help';
import {
  IconRefresh, IconEye, IconCalendar, IconHourglassEmpty,
  IconCircleCheck, IconCircleX, IconPlus, IconLayoutGrid,
  IconLayoutList, IconLayoutRows, IconSearch, IconAlertCircle, IconClock,
} from '@tabler/icons-react';
import { useStaffPortalLeaveService, type LeaveApprovalAskView } from '@actschurch/ccms-sdk';
import { useViewMode } from '@/stores/viewModeStore';
import { hrKeys } from '../utils/queryKeys';
import { LeaveStatusBadge } from '../components';
import { formatDate, formatDays, stripHtml } from '../utils/hrHelpers';
import { LEAVE_STATUS } from '../utils/hrConstants';
import { useHrBadgesStore } from '@/domain/badges/hrBadgesStore';
import { isAskOpen } from '../components/leave-approvals';

type LeaveRow = any & { id: number };
type LeaveViewMode = 'list' | 'item' | 'grid';
type StatsFilter = 'all' | 'pending' | 'approved' | 'rejected' | null;

/** The approver sent this leave back (duty cover or a question) and is waiting on the employee. */
const needsAction = (l: { statusID: number; openAsk?: LeaveApprovalAskView | null }) =>
  l.statusID === LEAVE_STATUS.PENDING && isAskOpen(l.openAsk);

function ActionNeededBadge() {
  return (
    <Badge size="xs" color="orange" variant="filled" style={{ textTransform: 'none' }}>
      Action needed
    </Badge>
  );
}

export default function MyLeavesPage() {
  const navigate = useNavigate();
  const leaveService = useStaffPortalLeaveService();

  const [viewMode, setViewMode] = useViewMode<LeaveViewMode>('hr.my.leaves', 'list');
  const [search, setSearch] = useState('');
  const [statsFilter, setStatsFilter] = useState<StatsFilter>(null);

  const { data: leaves = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: hrKeys.myLeaves(),
    queryFn: () => leaveService.getMyLeaves(),
  });

  // Keep the "My Leaves" menu badge in step with what this page shows.
  const setMyLeavesActionNeeded = useHrBadgesStore((s) => s.setMyLeavesActionNeeded);
  const actionNeededCount = useMemo(() => leaves.filter((l) => needsAction(l)).length, [leaves]);
  useEffect(() => {
    if (!isLoading && !error) setMyLeavesActionNeeded(actionNeededCount);
  }, [actionNeededCount, isLoading, error, setMyLeavesActionNeeded]);

  const allLeaves = useMemo<LeaveRow[]>(() => {
    const rows = (leaves as any[]).map((l) => ({ ...l, id: l.leaveID }));
    rows.sort((a, b) => {
      const ad = a.applicationDate ? new Date(a.applicationDate).getTime() : 0;
      const bd = b.applicationDate ? new Date(b.applicationDate).getTime() : 0;
      if (bd !== ad) return bd - ad;
      return (b.createdDateTime ?? 0) - (a.createdDateTime ?? 0);
    });
    return rows;
  }, [leaves]);

  const stats = useMemo(() => ({
    total: allLeaves.length,
    pending: allLeaves.filter((l) => l.statusID === LEAVE_STATUS.PENDING).length,
    approved: allLeaves.filter((l) => l.statusID === LEAVE_STATUS.APPROVED).length,
    rejected: allLeaves.filter((l) => l.statusID === LEAVE_STATUS.REJECTED).length,
  }), [allLeaves]);

  const filteredLeaves = useMemo(() => {
    let rows = allLeaves;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((l) => {
        const haystack = `${l.typeName ?? ''} ${stripHtml(l.reasonForLeave)} ${stripHtml(l.employeeComment)} ${l.statusName ?? ''}`.toLowerCase();
        return haystack.includes(q);
      });
    }
    if (statsFilter === 'pending') rows = rows.filter((l) => l.statusID === LEAVE_STATUS.PENDING);
    else if (statsFilter === 'approved') rows = rows.filter((l) => l.statusID === LEAVE_STATUS.APPROVED);
    else if (statsFilter === 'rejected') rows = rows.filter((l) => l.statusID === LEAVE_STATUS.REJECTED);
    return rows;
  }, [allLeaves, search, statsFilter]);

  const handleStatsClick = useCallback((filter: StatsFilter) => {
    setStatsFilter((prev) => (prev === filter || filter === 'all' ? null : filter));
  }, []);

  const handleView = useCallback((id: number) => navigate(`${id}`), [navigate]);

  const renderTypeBadge = (l: LeaveRow) => (
    <Badge
      size="xs"
      variant="light"
      style={l.typeColor ? { backgroundColor: l.typeColor + '22', color: l.typeColor } : undefined}
    >
      {l.typeName ?? `Type #${l.typeID}`}
    </Badge>
  );

  const columns: DataTableColumn<LeaveRow>[] = useMemo(
    () => [
      {
        key: 'typeName',
        label: 'Type',
        sortable: true,
        width: 200,
        render: (_v, l) => (
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
              size="md"
              variant="light"
              style={l.typeColor ? { color: l.typeColor, backgroundColor: l.typeColor + '22' } : undefined}
            >
              <IconCalendar size={16} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="sm" fw={500}>{l.typeName ?? `Type #${l.typeID}`}</Text>
              <Text size="xs" c="dimmed">#{l.leaveID}</Text>
            </Stack>
          </Group>
        ),
      },
      {
        key: 'startDate',
        label: 'Period',
        sortable: true,
        width: 240,
        render: (_v, l) => (
          <Stack gap={2}>
            <Text size="sm" style={{ whiteSpace: 'nowrap' }}>{formatDate(l.startDate)} to {formatDate(l.endDate)}</Text>
            <Group gap={4} wrap="nowrap">
              <IconClock size={11} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">{formatDays(l.numberOfDays)}</Text>
            </Group>
          </Stack>
        ),
      },
      {
        key: 'reasonForLeave',
        label: 'Reason',
        render: (v) => <Text size="sm" lineClamp={1}>{stripHtml(v as string | null)}</Text>,
      },
      {
        key: 'statusID',
        label: 'Status',
        width: 190,
        render: (_v, l) => (
          <Stack gap={4}>
            <Group gap={4} wrap="nowrap">
              <LeaveStatusBadge statusId={l.statusID} statusName={l.statusName} statusColor={l.statusColor} />
              {needsAction(l) && <ActionNeededBadge />}
            </Group>
            {l.statusID === LEAVE_STATUS.PENDING && l.approvalStageName && (
              <Text size="xs" c="dimmed" lineClamp={1}>{l.approvalStageName}</Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'applicationDate',
        label: 'Applied',
        sortable: true,
        width: 130,
        render: (v) => <Text size="sm">{formatDate(v as string | null)}</Text>,
      },
      {
        key: 'actions',
        label: '',
        width: 40,
        render: (_v, l) => (
          <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); handleView(l.leaveID); }}>
            <IconEye size={16} />
          </ActionIcon>
        ),
      },
    ],
    [handleView],
  );

  // Reset / no-op pagination — staff portal lists are small.
  useEffect(() => {}, [filteredLeaves.length]);

  const StatsCard = ({
    icon, color, value, label, filterValue,
  }: { icon: React.ReactNode; color: string; value: number; label: string; filterValue: StatsFilter }) => (
    <Card
      withBorder padding="md" radius="md"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderColor: statsFilter === filterValue ? `var(--mantine-color-${color}-5)` : undefined,
        backgroundColor: statsFilter === filterValue ? `var(--mantine-color-${color}-light)` : undefined,
      }}
      onClick={() => handleStatsClick(filterValue)}
    >
      <Group>
        <ThemeIcon size="lg" radius="md" variant={statsFilter === filterValue ? 'filled' : 'light'} color={color}>
          {icon}
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xl" fw={700}>{value}</Text>
          <Text size="xs" c="dimmed">{label}</Text>
        </Stack>
      </Group>
    </Card>
  );

  const renderItemView = () => (
    <Stack gap="sm">
      {filteredLeaves.map((l) => (
        <Paper key={l.id} p="md" withBorder style={{ cursor: 'pointer' }} onClick={() => handleView(l.leaveID)}>
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <ThemeIcon
                size="xl"
                radius="md"
                variant="light"
                style={l.typeColor ? { color: l.typeColor, backgroundColor: l.typeColor + '22' } : undefined}
              >
                <IconCalendar size={22} />
              </ThemeIcon>
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={600} truncate>{l.typeName ?? `Type #${l.typeID}`}</Text>
                  <Text size="xs" c="dimmed">#{l.leaveID}</Text>
                </Group>
                <Text size="xs" c="dimmed">{formatDate(l.startDate)} to {formatDate(l.endDate)} · {formatDays(l.numberOfDays)}</Text>
                {stripHtml(l.reasonForLeave) && <Text size="xs" c="dimmed" lineClamp={1}>{stripHtml(l.reasonForLeave)}</Text>}
              </Stack>
            </Group>
            <Group gap="xs" wrap="nowrap">
              {needsAction(l) && <ActionNeededBadge />}
              <LeaveStatusBadge statusId={l.statusID} statusName={l.statusName} statusColor={l.statusColor} />
              <ActionIcon variant="subtle" onClick={(ev: React.MouseEvent) => { ev.stopPropagation(); handleView(l.leaveID); }}>
                <IconEye size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>
      ))}
    </Stack>
  );

  const renderGridView = () => (
    <Grid>
      {filteredLeaves.map((l) => (
        <Grid.Col key={l.id} span={{ base: 12, xs: 6, md: 4, lg: 3 }}>
          <Card padding="lg" withBorder style={{ cursor: 'pointer', height: '100%' }} onClick={() => handleView(l.leaveID)}>
            <Stack gap="sm">
              <Group justify="space-between" wrap="nowrap">
                <ThemeIcon
                  size="lg"
                  radius="md"
                  variant="light"
                  style={l.typeColor ? { color: l.typeColor, backgroundColor: l.typeColor + '22' } : undefined}
                >
                  <IconCalendar size={20} />
                </ThemeIcon>
                <Group gap={4} wrap="nowrap">
                  {needsAction(l) && <ActionNeededBadge />}
                  <LeaveStatusBadge statusId={l.statusID} statusName={l.statusName} statusColor={l.statusColor} />
                </Group>
              </Group>
              <Stack gap={2}>
                <Text size="sm" fw={600} lineClamp={1}>{l.typeName ?? `Type #${l.typeID}`}</Text>
                <Text size="xs" c="dimmed">#{l.leaveID}</Text>
              </Stack>
              <Divider />
              <Group gap="xs" wrap="wrap">{renderTypeBadge(l)}</Group>
              <Stack gap={0}>
                <Text size="xs" c="dimmed">{formatDate(l.startDate)} to {formatDate(l.endDate)}</Text>
                <Text size="xs" c="dimmed">{formatDays(l.numberOfDays)}</Text>
              </Stack>
              {stripHtml(l.reasonForLeave) && <Text size="xs" c="dimmed" lineClamp={2}>{stripHtml(l.reasonForLeave)}</Text>}
            </Stack>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );

  const loading = isLoading || isFetching;

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={0}>
            <Title order={2}>My Leave Requests</Title>
            <Text size="sm" c="dimmed">Apply for leave and track approvals.</Text>
          </Stack>
          <Group gap="sm" data-help-id="hr.my-leaves.actions">
            <Tooltip label="Refresh">
              <ActionIcon variant="subtle" onClick={() => refetch()} loading={loading}>
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <Button leftSection={<IconPlus size={16} />} component={Link} to="new" data-help-id="hr.my-leaves.new-leave">New Leave Request</Button>
            <PageHelpButton pageKey="hr.my-leaves" />
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 2, md: 4 }} data-help-id="hr.my-leaves.stats">
          <StatsCard icon={<IconCalendar size={20} />}        color="violet" value={stats.total}    label="Total"    filterValue="all" />
          <StatsCard icon={<IconHourglassEmpty size={20} />}  color="yellow" value={stats.pending}  label="Pending"  filterValue="pending" />
          <StatsCard icon={<IconCircleCheck size={20} />}     color="green"  value={stats.approved} label="Approved" filterValue="approved" />
          <StatsCard icon={<IconCircleX size={20} />}         color="red"    value={stats.rejected} label="Rejected" filterValue="rejected" />
        </SimpleGrid>

        <Paper withBorder p="md" radius="md" data-help-id="hr.my-leaves.toolbar">
          <Group justify="space-between" wrap="wrap">
            <TextInput
              placeholder="Search leaves"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ minWidth: 240, flex: 1 }}
            />
            <Group gap="sm">
              <Text size="sm" c="dimmed">
                {loading && allLeaves.length === 0 ? 'Loading' : `${filteredLeaves.length} of ${allLeaves.length}`}
              </Text>
              <SegmentedControl
                data-help-id="hr.my-leaves.view-switch"
                value={viewMode}
                onChange={(v) => setViewMode(v as LeaveViewMode)}
                size="xs"
                data={[
                  { value: 'list', label: <Tooltip label="Table"><Group gap={0} px={4}><IconLayoutList size={14} /></Group></Tooltip> },
                  { value: 'item', label: <Tooltip label="Item"><Group gap={0} px={4}><IconLayoutRows size={14} /></Group></Tooltip> },
                  { value: 'grid', label: <Tooltip label="Grid"><Group gap={0} px={4}><IconLayoutGrid size={14} /></Group></Tooltip> },
                ]}
              />
            </Group>
          </Group>
        </Paper>

        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load your leave requests.</Alert>}

        {actionNeededCount > 0 && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Your approver needs something from you" data-help-id="hr.my-leaves.action-needed">
            {actionNeededCount === 1
              ? 'One leave is waiting on you. Open the one tagged Action needed to reply.'
              : `${actionNeededCount} leaves are waiting on you. Open the ones tagged Action needed to reply.`}
          </Alert>
        )}

        <div data-help-id="hr.my-leaves.list">
        {loading && allLeaves.length === 0 && viewMode !== 'list' ? (
          <Card withBorder>
            <Stack align="center" gap="xs" py="xl"><Loader /><Text size="sm" c="dimmed">Loading leaves…</Text></Stack>
          </Card>
        ) : filteredLeaves.length === 0 && viewMode !== 'list' ? (
          <Card withBorder>
            <Stack align="center" gap="sm" py="xl">
              <IconCalendar size={32} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">{search || statsFilter ? 'No leaves match these filters.' : "You haven't submitted any leave requests yet."}</Text>
              <Button component={Link} to="new" variant="light" leftSection={<IconPlus size={14} />}>Apply for leave</Button>
            </Stack>
          </Card>
        ) : viewMode === 'list' ? (
          <Card withBorder p={0}>
            <DataTable<LeaveRow>
              columns={columns}
              data={filteredLeaves}
              loading={loading}
              enableInternalSort
              onRowClick={(row) => handleView(row.leaveID)}
              emptyMessage={search || statsFilter ? 'No leaves match these filters.' : "You haven't submitted any leave requests yet."}
              striped
              highlightOnHover
            />
          </Card>
        ) : viewMode === 'item' ? renderItemView() : renderGridView()}
        </div>
      </Stack>
    </FadeIn>
  );
}
